<template>
    <div class="login">

        <div class="card">

            <!-- Maintenance mode — shown when deployment lock is active and admin link not clicked -->
            <div v-if="maintenanceMode && !showAdminLogin" class="card-body maintenance-message">
                <h5 class="card-title text-center">System Maintenance</h5>
                <hr>
                <p class="text-center">{{ $store.state.deploymentLockMessage }}</p>
                <div class="text-center mt-3">
                    <a href="#" @click.prevent="showAdminLogin = true" class="admin-login-link">Administrator Login</a>
                </div>
            </div>

            <!-- Normal login form — shown when not in maintenance mode, or admin link was clicked -->
            <template v-if="!maintenanceMode || showAdminLogin">
                <div class="card-header">
                    <h5 class="card-title text-center"  v-if="$route.query.code">Activate Account</h5>
                    <h5 class="card-title text-center" v-if="!$route.query.code">Login</h5>
                    <hr>
                </div>
                <form class="card-body" @submit.prevent="submit">
                    <basic-text-input class="mb-3" label="Email"    placeholder="Email"    v-model="email" :focus="true" />
                    <div v-if="$v.email && $v.email.$error && $v.email.$dirty ">
                        <div class="validation-error" >Please enter an email</div>
                    </div>
                    <template v-if="userAlreadyHasPassword">
                        <basic-text-input class="mb-3" label="Password" placeholder="Password" v-model="password" type="password"  />
                        <div v-if="$v.password && $v.password.$error && $v.password.$dirty ">
                            <div class="validation-error" >Please enter your password</div>
                        </div>
                    </template>
                    <BasicTextInput
                        v-if="!userAlreadyHasPassword"
                        class="mb-3"
                        label="Password"
                        placeholder="Password"
                        :validator="$v.newPassword"
                        :validatorMsgMap="passwordMsgMap"
                        v-model="newPassword"
                        type="password" />
                    <basic-text-input v-if="!userAlreadyHasPassword" class="mb-3" label="Confirm Password" placeholder="Password" v-model="passwordCheck" type="password"  />
                    <div v-if="$v.passwordCheck && $v.passwordCheck.$error && $v.passwordCheck.$dirty ">
                        <div class="validation-error" >Passwords must match</div>
                    </div>
                    <div v-if="!$route.query.code" class="d-flex justify-content-end mb-3">
                        <router-link :to="{ name: 'ForgotPassword' }">Forgot Password</router-link>
                    </div>
                    <button v-if="$route.query.code" class="btn btn-primary btn-block"  >Activate</button>
                    <button v-if="!$route.query.code" class="btn btn-primary mb-3 btn-block" >Login</button>
                    <!-- <Checkbox v-if="!$route.query.code" v-model="rememberUser" label="Remember Me"/> -->
                </form>

                <!-- Permanent single-user licensing reminder (#2026-107). Final client-approved wording. -->
                <p class="tos-blurb text-center">
                    By logging on you are agreeing to the
                    <a href="http://dentacad.com/Support/QuickAnswers/TermsofService.html" target="_blank" rel="noopener">Terms Of Service</a>
                    including that you are logging on to an account you are authorized to use. No accounts named for a particular person (i.e. John Smith) may be used by any other person under any circumstances. Abuse may result in additional charges or account suspension.
                </p>

                <Modal ref="overrideSessionModal">
                    <div class="card modal-wrapper p-4">
                        <p class="m-0">Active session found for this user.  Override and login anyway?</p>
                        <div class="btn-wrapper center row">
                            <button class="btn btn-secondary mb-2 col-12" @click="toggleOverrideSession">Cancel</button>
                            <button @click="loginOverride" class="btn btn-primary col-12">Login</button>
                        </div>
                    </div>
                </Modal>
            </template>

        </div>
    </div>
</template>

<script>
    import { Auth } from '@/services';
    import FingerprintJS from '@fingerprintjs/fingerprintjs';

    import { required, requiredIf, sameAs, minLength, helpers }  from 'vuelidate/lib/validators';

    import { validatorMsgMapBase } from "@/modules/helpers";

    const hasDigit           = helpers.regex('hasDigit', /\d/);
    const hasUppercase       = helpers.regex('hasUppercase', /[A-Z]/);
    const hasLowercase       = helpers.regex('hasLowercase', /[a-z]/);
    const hasNonAlphanumeric = helpers.regex('hasNonAlphanumeric', /\W/);

    // Kicked off at module load so the agent is warmed up by the time the user submits the form.
    // Used only as a device signal for the account-sharing detection audit log (#2026-107) --
    // no detection logic reads this yet, and a failure here must never block login.
    const fingerprintPromise = FingerprintJS.load();
    async function getFingerprintHash() {
        try {
            const fp = await fingerprintPromise;
            const result = await fp.get();
            return result.visitorId;
        } catch (e) {
            return '';
        }
    }

    export default {
        name: "Login",
        data: () => {
            return {
                email: '',
                password: '',
                newPassword: '',
                passwordCheck: '',
                // rememberUser: '',
                validatorMsgMapBase: validatorMsgMapBase,
                showAdminLogin: false,
            }
        },
        watch: {
            maintenanceMode(val) {
                if (!val) this.showAdminLogin = false;
            }
        },
        validations() {
            return {
                email: {
                    required
                }, 
                password: {
                    required
                },
                newPassword:    {
                   required,
                   minLength: minLength(8),
                   hasUppercase,
                   hasLowercase,
                   hasDigit,
                   hasNonAlphanumeric,
               },
                passwordCheck: {
                    required : requiredIf(function(){
        	              return (!this.userAlreadyHasPassword); 
                    }),
                    sameAsPassword: sameAs('newPassword'),
                }
            }
        },
        methods: {
            toggleOverrideSession(){
                this.$refs.overrideSessionModal.toggle()
            },
            invalidLogin(){
                if(this.userAlreadyHasPassword){
                    return this.$v.email.$invalid || this.$v.password.$invalid
                } else {
                    return this.$v.email.$invalid || this.$v.passwordCheck.$invalid || this.$v.newPassword.$invalid
                }
                return this.$v.email.$invalid || (this.userAlreadyHasPassword && this.$v.password.$invalid) || (!this.userAlreadyHasPassword && (this.$v.passwordCheck.$invalid || this.$v.newPassword.$invalid))
            },
            submit(){
                this.$v.email.$touch();
                if(!this.userAlreadyHasPassword){
                    this.$v.newPassword.$touch();
                    this.$v.passwordCheck.$touch(); 
                } else {
                    this.$v.password.$touch();
                }
                if (this.invalidLogin()){
                    this.$store.dispatch('ADD_NOTIFICATION', { text: 'See Validation Errors', type: 'error' });
                    return;
                }
                if (this.$route.query.code){
                    this.activateUser();
                } else {
                    this.login();
                }
            },
            activateUser(){
                let code = this.$route.query.code;
                if (!code) {
                    this.$store.dispatch('ADD_NOTIFICATION', { text: 'Activation Code is missing', type: 'error' });
                    return;
                }

                let data = {
                    Email:    this.email,
                    Password: this.userAlreadyHasPassword ? this.password : this.newPassword,
                    Token:    code,
                }
                Auth.activateUser(data).then(res => {
                    this.login();
                }).catch(err => {
                    this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message, type: 'error' });
                })

            },
            async login(){
                let password = this.userAlreadyHasPassword ? this.password : this.newPassword
                let cleanPassword = encodeURIComponent(password)
                let fingerprintHash = await getFingerprintHash();
                let data = `grant_type=password&username=${this.email}&password=${cleanPassword}&fingerprinthash=${encodeURIComponent(fingerprintHash)}`; // Yes, I know this is weird, the .NET framework library could only accept token request with a x-www-form-urlencoded format, which requires query string type param passing.
                Auth.login(data).then(res => {
                    this.$store.dispatch('SET_TOKEN', res).then(() => {
                        this.$router.push({ name: 'DashboardHome' });
                    });
                }).catch(err => {
                    if(err.response.data === 'User is locked out.') {
                        this.$store.dispatch('ADD_NOTIFICATION', { text: 'Your account has been locked due to exceeding a maximum number of failed logon attempts.  This is to prevent illicit access to your account.  Your account will unlock automatically in 5 minutes.',
                         type: 'error', timeout: 900000 });
                    } else if (err.response.data === 'User is disabled.') {
                        this.$store.dispatch('ADD_NOTIFICATION', { text: 'Your account has been disabled.  If you believe this is in error, please contact SmartSales Support.',
                         type: 'error', timeout: 900000 });
                    } else if (err.response.data?.error === 'deployment_lock') {
                        this.showAdminLogin = false;
                    } else if(err.response.data === 'Active sessions exist') {
                        this.toggleOverrideSession();
                    } else {
                        this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data, type: 'error' });
                    }
                })
            },
            async loginOverride() {
                let password = this.userAlreadyHasPassword ? this.password : this.newPassword
                let cleanPassword = encodeURIComponent(password)
                let fingerprintHash = await getFingerprintHash();
                let data = `grant_type=password&username=${this.email}&password=${cleanPassword}&sessionoverride=true&fingerprinthash=${encodeURIComponent(fingerprintHash)}`;

                this.toggleOverrideSession();

                Auth.login(data).then(response => {
                    this.$store.dispatch('SET_TOKEN', response).then(() => {
                        this.$router.push({ name: 'DashboardHome' });
                    });
                }).catch(err => {
                    if(err.response.data === 'User is locked out.') {
                        this.$store.dispatch('ADD_NOTIFICATION', { text: 'Your account has been locked due to exceeding a maximum number of failed logon attempts.  This is to prevent illicit access to your account.  Your account will unlock automatically in 5 minutes.',
                         type: 'error', timeout: 900000 });
                    } else if (err.response.data === 'User is disabled.') {
                        this.$store.dispatch('ADD_NOTIFICATION', { text: 'Your account has been disabled.  If you believe this is in error, please contact SmartSales Support.',
                         type: 'error', timeout: 900000 });
                    } else {
                        this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data, type: 'error' });
                    }             
                })
            }
        },
        computed:{
            maintenanceMode() {
                return this.$store.state.deploymentLockActive;
            },
            userAlreadyHasPassword(){
                return !this.$route.query.type || this.$route.query.type === 'dealer'
            },
            passwordMsgMap(){
                return { ...this.validatorMsgMapBase,
                    minLength: 'Must be at least 8 Characters'};
            },
        }
    }
</script>

<style scoped lang="scss">
    .login {
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        .card {
            max-width: 500px;
            min-width: 350px;
        }
    }

    .tos-blurb {
        padding: 0 1.5rem 1.5rem;
        margin-bottom: 0;
        font-size: 0.75rem;
        color: #555;
    }

    .maintenance-message {
        padding: 2rem;

        p {
            color: #555;
            margin-bottom: 0;
        }

        .admin-login-link {
            font-size: 0.8rem;
            color: #aaa;
        }
    }
</style>
